import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Check, ChevronDown, GripVertical, Trash2, Undo2 } from "lucide-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Modal } from "@/components/Modal";
import { useSettings } from "@/hooks/useSettings";
import { useOpenWeeks } from "@/hooks/useWeeks";
import {
  groupMoveTargets,
  type IsoWeek,
  isoWeekMonday,
  type MoveTarget,
  type MoveWindow,
  moveTargetShortLabel,
  moveWindow,
} from "@/lib/days";
import {
  createWeek,
  findWeekByYearAndNumber,
  updateWeek,
} from "@/lib/repos/weeks";
import type {
  Todo,
  TodoListConfig,
  TodoListDayData,
  WeekData,
  WidgetWeekEntry,
} from "@/lib/schema";
import {
  append,
  closeDay,
  checkedCount as countChecked,
  deletedTodos,
  doneTodos as doneInCompletionOrder,
  isFinished,
  loggingCompleted as isLoggingCompleted,
  moveIn,
  moveOut,
  openTodos,
  remove,
  rename,
  reopenDay,
  reorder,
  restore,
  toggleChecked as toggle,
  visibleTodos,
  withTodos,
} from "@/lib/todos";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { MoveTodosModal, type WeekStatus } from "./MoveTodosModal";

type Props = WidgetDayViewProps<TodoListConfig>;

const PREVIEW_TODOS: Todo[] = [
  { id: "preview-1", label: "Eksempel-oppgave", checked: false },
  { id: "preview-2", label: "Fullført eksempel", checked: true },
];

type ModalState =
  | { kind: "none" }
  | { kind: "move" }
  | { kind: "error"; title: string; body: string };

interface WeekRef {
  id: string;
  hasWidget: boolean;
}

export function DayView({
  config,
  data,
  dayKey,
  onChangeDay,
  readOnly = false,
  weekId,
  week,
}: Props) {
  const realData = (data ?? { todos: [] }) as TodoListDayData;
  const stored = realData.todos;
  const showPreview = readOnly && visibleTodos(stored).length === 0;
  const todos: readonly Todo[] = showPreview ? PREVIEW_TODOS : stored;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [addValue, setAddValue] = useState("");
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashDay, setTrashDay] = useState(dayKey);
  const { settings } = useSettings();

  // Only ever fed by `@/lib/todos`, whose derivations are `readonly Todo[]`
  // and so cannot reach this — a filtered list written back here would drop
  // every tombstone and empty the trash in silence (docs/adr/0020). Routed
  // through `withTodos` so that a write which leaves an open item on a closed
  // day also reopens it (docs/adr/0024).
  const writeTodos = (next: Todo[]) => {
    onChangeDay(withTodos(realData, next));
  };

  const finished = isFinished(realData);
  const toggleFinished = () => {
    if (readOnly) return;
    onChangeDay(finished ? reopenDay(realData) : closeDay(realData));
  };

  const toggleChecked = (id: string) => {
    if (readOnly) return;
    writeTodos(toggle(stored, id));
  };

  const deleteTodo = (id: string) => {
    if (readOnly) return;
    writeTodos(remove(stored, id));
  };

  const restoreTodo = (id: string) => {
    if (readOnly) return;
    writeTodos(restore(stored, id));
  };

  const startEdit = (t: Todo) => {
    if (readOnly) return;
    setEditingId(t.id);
    setEditingValue(t.label);
  };

  const commitEdit = () => {
    if (editingId === null) return;
    const trimmed = editingValue.trim();
    if (trimmed.length > 0) writeTodos(rename(stored, editingId, trimmed));
    setEditingId(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleAdd = () => {
    if (readOnly) return;
    const trimmed = addValue.trim();
    if (trimmed.length === 0) return;
    writeTodos(append(stored, trimmed));
    setAddValue("");
  };

  // Manuell rekkefølge (CONTEXT.md): `stored` holds the one order the user
  // dragged into, ticked and unticked and deleted interleaved. Both splits
  // below are applied at render and never written back, which is what lets an
  // unticked item — or a restored one — return beside exactly the neighbours
  // it had (docs/adr/0015, docs/adr/0019, docs/adr/0020).
  const notDone = openTodos(todos);
  const done = doneInCompletionOrder(todos);
  const deleted = deletedTodos(todos);
  const visibleCount = visibleTodos(todos).length;

  // The card is keyed on widget id rather than date, so days re-render rather
  // than remount: without this, a trash opened on Monday stays open on Tuesday.
  // Adjusted during render rather than in an effect — the reset is a function
  // of dayKey changing, and an effect would have had to list a dependency its
  // body never reads.
  if (trashDay !== dayKey) {
    setTrashDay(dayKey);
    setTrashOpen(false);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // A drag moves exactly one item and leaves every other pair's relative order
  // intact — done items and tombstones among them (docs/adr/0015).
  const handleDragEnd = (e: DragEndEvent) => {
    if (readOnly || !e.over || e.active.id === e.over.id) return;
    writeTodos(reorder(stored, String(e.active.id), String(e.over.id)));
  };

  const uncheckedCount = notDone.length;

  // Ferdiglogget (CONTEXT.md): nothing unchecked, and either something visible
  // on the list or the day closed by «Ferdig for i dag». A bare empty list
  // claims nothing — one emptied by deleting included, since deleting is not
  // doing — but one emptied by a move does, because the move closed it
  // (docs/adr/0024).
  const checkedCount = countChecked(todos);
  const loggingCompleted = !readOnly && isLoggingCompleted(realData);

  const { open, toggle: toggleCard } = useAutoCollapse(
    dayKey,
    loggingCompleted,
  );
  // A closed empty day has nothing to list but does have something to fold
  // away to: the summary that says why it is green.
  const collapsible = !readOnly && (visibleCount > 0 || finished);
  const collapsed = collapsible && !open;

  // Flyttevindu (docs/adr/0023). The start is read from the wall clock at
  // render, so a card left open across midnight offers a stale window until
  // something re-renders; the end is read from the weeks that exist.
  const startDate = week?.startDate;
  const { weeks: openWeeks, loading: weeksLoading } = useOpenWeeks();
  // A week that dropped this huskeliste can receive nothing, so reaching out
  // to it would buy a week of dead pills and no target (docs/adr/0023).
  const reachableWeeks: IsoWeek[] = useMemo(
    () =>
      openWeeks
        .filter((w) =>
          w.data.widgets.some((e) => e.config.id === config.id && "days" in e),
        )
        .map((w) => ({ year: w.data.year, weekNumber: w.data.weekNumber })),
    [openWeeks, config.id],
  );
  const span: MoveWindow = useMemo(
    () =>
      startDate
        ? moveWindow(startDate, dayKey, new Date(), reachableWeeks)
        : { targets: [], viewedDate: null },
    [startDate, dayKey, reachableWeeks],
  );
  const moveTargets = span.targets;
  const groups = useMemo(() => groupMoveTargets(span, new Date()), [span]);

  const lookupWeek = useCallback(
    async (year: number, weekNumber: number): Promise<WeekRef | null> => {
      const found = await findWeekByYearAndNumber(year, weekNumber);
      if (!found) return null;
      return {
        id: found.id,
        hasWidget: found.data.widgets.some(
          (e) => e.config.id === config.id && "days" in e,
        ),
      };
    },
    [config.id],
  );

  const [weekRefs, setWeekRefs] = useState<Map<string, WeekRef | null>>(
    () => new Map(),
  );
  useEffect(() => {
    if (readOnly || groups.length === 0) return;
    let cancelled = false;
    void (async () => {
      const next = new Map<string, WeekRef | null>();
      for (const g of groups) {
        next.set(
          `${g.year}-${g.weekNumber}`,
          await lookupWeek(g.year, g.weekNumber),
        );
      }
      if (!cancelled) setWeekRefs(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [groups, lookupWeek, readOnly]);

  const weekStatus = (year: number, weekNumber: number): WeekStatus => {
    const ref = weekRefs.get(`${year}-${weekNumber}`);
    // Not looked up yet: assume usable rather than flashing a warning.
    if (ref === undefined) return "exists";
    if (ref === null) return "missing";
    return ref.hasWidget ? "exists" : "no-widget";
  };

  const quickTarget: MoveTarget | undefined = moveTargets[0];
  // The window's first week having dropped this widget blocks only the quick
  // button; the popup can still offer every other day.
  //
  // Both controls also wait for the open weeks to arrive: until they do the
  // end is a guess, and a window that silently lengthens a beat after you read
  // it is worse than one that admits it is not ready (docs/adr/0023).
  const quickDisabled =
    weeksLoading ||
    uncheckedCount === 0 ||
    !quickTarget ||
    weekStatus(quickTarget.year, quickTarget.weekNumber) === "no-widget";
  const popupDisabled = weeksLoading || uncheckedCount === 0;
  // Both labels name the subset outright. No count: the popup can move one of
  // six, so a number on the button would be a promise it does not keep.
  const quickLabel = quickTarget
    ? `Flytt ufullførte til ${moveTargetShortLabel(quickTarget)}`
    : "Flytt ufullførte til i morgen";

  /** Resolve the target week, creating it when the window said "(opprettes)". */
  const resolveTargetWeek = async (target: MoveTarget): Promise<WeekRef> => {
    const found = await lookupWeek(target.year, target.weekNumber);
    if (found) {
      if (!found.hasWidget) {
        throw new Error(
          `Widgeten finnes ikke i uke ${target.weekNumber}. Kan ikke flytte.`,
        );
      }
      return found;
    }
    if (!settings) throw new Error("Innstillinger er ikke lastet. Prøv igjen.");
    const created = await createWeek({
      year: target.year,
      weekNumber: target.weekNumber,
      startDate: isoWeekMonday(target.year, target.weekNumber),
      settings,
    });
    const entry = created.data.widgets.find((e) => e.config.id === config.id);
    if (!entry || !("days" in entry)) {
      throw new Error(
        `Uke ${target.weekNumber} ble opprettet, men widgeten finnes ikke i den. Kan ikke flytte.`,
      );
    }
    return { id: created.id, hasWidget: true };
  };

  // Arrivals are open items, so a closed target day reopens.
  const appendTodos = (
    entry: WidgetWeekEntry,
    day: MoveTarget["dayKey"],
    moving: Todo[],
  ): WidgetWeekEntry => {
    if (entry.config.id !== config.id || !("days" in entry)) return entry;
    const dest = entry.days[day] as TodoListDayData;
    return {
      ...entry,
      days: { ...entry.days, [day]: moveIn(dest, moving) },
    };
  };

  // A move that leaves nothing open behind closes the day it emptied
  // (docs/adr/0024) — whichever control did the moving.
  const removeTodos = (
    entry: WidgetWeekEntry,
    ids: Set<string>,
  ): WidgetWeekEntry => {
    if (entry.config.id !== config.id || !("days" in entry)) return entry;
    const source = entry.days[dayKey] as TodoListDayData;
    return {
      ...entry,
      days: { ...entry.days, [dayKey]: moveOut(source, ids) },
    };
  };

  const performMove = async (ids: string[], target: MoveTarget) => {
    if (!weekId) return;
    const chosen = new Set(ids);
    // Open items only: a tombstone carried to another day would arrive as a
    // deleted todo nobody there ever deleted.
    const moving = openTodos(stored).filter((t) => chosen.has(t.id));
    if (moving.length === 0) return;
    const movingIds = new Set(moving.map((t) => t.id));
    const targetWeek = await resolveTargetWeek(target);

    if (targetWeek.id === weekId) {
      // Same week: one patch, so the source day never reads a stale copy of
      // itself between two writes.
      await updateWeek(weekId, (prev: WeekData) => ({
        ...prev,
        widgets: prev.widgets.map((w) =>
          appendTodos(removeTodos(w, movingIds), target.dayKey, moving),
        ),
      }));
      return;
    }
    await updateWeek(targetWeek.id, (prev: WeekData) => ({
      ...prev,
      widgets: prev.widgets.map((w) => appendTodos(w, target.dayKey, moving)),
    }));
    await updateWeek(weekId, (prev: WeekData) => ({
      ...prev,
      widgets: prev.widgets.map((w) => removeTodos(w, movingIds)),
    }));
  };

  const runMove = async (ids: string[], target: MoveTarget) => {
    try {
      await performMove(ids, target);
      setModal({ kind: "none" });
    } catch (error) {
      setModal({
        kind: "error",
        title: "Kunne ikke flytte",
        body: error instanceof Error ? error.message : "Ukjent feil.",
      });
    }
  };

  const handleQuickMove = () => {
    if (quickDisabled || !quickTarget) return;
    void runMove(
      notDone.map((t) => t.id),
      quickTarget,
    );
  };

  const rowProps = {
    readOnly,
    editingId,
    editingValue,
    setEditingValue,
    toggleChecked,
    startEdit,
    commitEdit,
    cancelEdit,
  };

  return (
    <section
      className={`widget-card todo-list${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggleCard}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          {/* A closed empty day has no count worth stating — "0/0 gjort"
              reads as a bug — so it states the claim instead. */}
          <span className="summary-line">
            {visibleCount === 0
              ? "Ferdig for i dag"
              : `${checkedCount}/${visibleCount} gjort`}
          </span>
        </div>
      ) : null}
      {/* The bar answers "what happens to today's list now". While anything is
          open, that is the two move controls; once the list is empty, it is
          «Ferdig for i dag» in their place — styled as the primary button so
          it reads as a different control and not a third grey one. Controls
          that cannot act are absent rather than disabled, and a list where
          everything is ticked has no bar at all: the card is green on its
          own there (docs/adr/0024). */}
      {!readOnly && !collapsed && uncheckedCount > 0 ? (
        <div className="todo-move-bar" data-testid={`move-bar-${config.id}`}>
          <button
            type="button"
            className="btn ghost"
            onClick={handleQuickMove}
            disabled={quickDisabled}
            data-testid={`move-unchecked-button-${config.id}`}
          >
            {quickLabel}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setModal({ kind: "move" })}
            disabled={popupDisabled}
            data-testid={`move-to-button-${config.id}`}
          >
            Flytt ufullførte til…
          </button>
        </div>
      ) : null}
      {!readOnly && !collapsed && visibleCount === 0 ? (
        <div className="todo-move-bar" data-testid={`move-bar-${config.id}`}>
          <button
            type="button"
            className={finished ? "btn ghost" : "btn-primary"}
            onClick={toggleFinished}
            data-testid={`todo-list-finish-button-${config.id}`}
          >
            {finished ? "Åpne dagen igjen" : "Ferdig for i dag"}
          </button>
        </div>
      ) : null}
      <div className="widget-card-body">
        <div
          className="todo-items"
          data-testid={`todo-list-items-${config.id}`}
        >
          {visibleCount === 0 ? (
            <p className="hint" data-testid={`todo-list-empty-${config.id}`}>
              {/* "enda" says more is coming, which a closed day denies. */}
              {finished ? "Ingen oppgaver i dag." : "Ingen oppgaver enda."}
            </p>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={notDone.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {notDone.map((t, i) => (
                    <SortableTodoRow
                      key={t.id}
                      todo={t}
                      // The row above the rule drops its own dashed one, so
                      // the split reads as one divider rather than two.
                      lastBeforeRule={
                        i === notDone.length - 1 && done.length > 0
                      }
                      deleteTodo={deleteTodo}
                      {...rowProps}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {notDone.length > 0 && done.length > 0 ? (
                <hr
                  className="todo-done-rule"
                  data-testid={`todo-done-divider-${config.id}`}
                />
              ) : null}
              {/* Done rows carry neither handle nor delete: reordering them has
                  no visible effect (docs/adr/0015), and removing one means
                  unticking it first (docs/adr/0020). */}
              {done.map((t) => (
                <TodoRow key={t.id} todo={t} {...rowProps} />
              ))}
            </>
          )}
        </div>
        {!readOnly ? (
          <div className="cl-add">
            <input
              type="text"
              placeholder="Legg til oppgave"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              data-testid={`todo-list-input-${config.id}`}
            />
            <button
              type="button"
              onClick={handleAdd}
              data-testid={`todo-list-add-button-${config.id}`}
            >
              + Legg til
            </button>
          </div>
        ) : null}
        {/* Slettet oppgave (CONTEXT.md). Costs no room until something has
            been deleted, and sits below the add field so the list keeps the
            control that feeds it as its nearest neighbour. */}
        {!readOnly && deleted.length > 0 ? (
          <div className={`todo-trash${trashOpen ? " open" : ""}`}>
            <button
              type="button"
              className="todo-trash-toggle"
              onClick={() => setTrashOpen((o) => !o)}
              aria-expanded={trashOpen}
              data-testid={`todo-deleted-disclosure-${config.id}`}
            >
              <ChevronDown className="chev" size={14} />
              Slettet ({deleted.length})
            </button>
            {trashOpen ? (
              <div
                className="todo-trash-list"
                data-testid={`todo-deleted-list-${config.id}`}
              >
                {deleted.map((t) => (
                  <div
                    key={t.id}
                    className="todo-trash-row"
                    data-testid={`todo-deleted-row-${t.id}`}
                  >
                    <span className="todo-trash-label">{t.label}</span>
                    <button
                      type="button"
                      className="icon-btn-restore"
                      onClick={() => restoreTodo(t.id)}
                      aria-label="Gjenopprett"
                      data-testid={`todo-restore-${t.id}`}
                    >
                      <Undo2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <MoveTodosModal
        open={modal.kind === "move"}
        widgetId={config.id}
        todos={notDone}
        groups={groups}
        weekStatus={weekStatus}
        onCancel={() => setModal({ kind: "none" })}
        onConfirm={runMove}
      />
      <Modal
        open={modal.kind === "error"}
        onClose={() => setModal({ kind: "none" })}
        data-testid={`move-error-modal-${config.id}`}
        size="sm"
      >
        {modal.kind === "error" ? (
          <>
            <h2>{modal.title}</h2>
            <p className="modal-body">{modal.body}</p>
          </>
        ) : null}
        <div className="modal-actions">
          <button
            type="button"
            className="btn"
            onClick={() => setModal({ kind: "none" })}
            data-testid="move-error-close"
          >
            OK
          </button>
        </div>
      </Modal>
    </section>
  );
}

interface RowProps {
  todo: Todo;
  lastBeforeRule?: boolean;
  readOnly: boolean;
  editingId: string | null;
  editingValue: string;
  setEditingValue: (v: string) => void;
  toggleChecked: (id: string) => void;
  /** Absent on done rows, which offer no way to delete (docs/adr/0020). */
  deleteTodo?: (id: string) => void;
  startEdit: (t: Todo) => void;
  commitEdit: () => void;
  cancelEdit: () => void;
}

/** A not-done row: the only kind that carries a drag handle (docs/adr/0015). */
function SortableTodoRow(props: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.todo.id, disabled: props.readOnly });
  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: transition ?? undefined,
  };
  return (
    <TodoRow
      {...props}
      rowRef={setNodeRef}
      style={style}
      dragging={isDragging}
      handle={
        props.readOnly ? null : (
          <button
            type="button"
            className="cl-drag icon-btn-drag"
            aria-label="Endre rekkefølge"
            data-testid={`todo-drag-handle-${props.todo.id}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={14} />
          </button>
        )
      }
    />
  );
}

function TodoRow({
  todo: t,
  lastBeforeRule = false,
  readOnly,
  editingId,
  editingValue,
  setEditingValue,
  toggleChecked,
  deleteTodo,
  startEdit,
  commitEdit,
  cancelEdit,
  rowRef,
  style,
  dragging = false,
  handle = null,
}: RowProps & {
  rowRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  dragging?: boolean;
  handle?: React.ReactNode;
}) {
  const isEditing = editingId === t.id;
  return (
    <div
      ref={rowRef}
      style={style}
      className={`cl-item${t.checked ? " done" : ""}${dragging ? " dragging" : ""}${lastBeforeRule ? " last-before-rule" : ""}`}
      data-testid={`todo-row-${t.id}`}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: custom-styled checkbox + label. */}
      <span
        className={`check${t.checked ? " on" : ""}`}
        role="checkbox"
        aria-checked={t.checked}
        aria-disabled={readOnly}
        tabIndex={readOnly ? -1 : 0}
        onClick={readOnly ? undefined : () => toggleChecked(t.id)}
        onKeyDown={
          readOnly
            ? undefined
            : (e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  toggleChecked(t.id);
                }
              }
        }
        data-testid={`todo-checkbox-${t.id}`}
      >
        <Check size={14} />
      </span>
      {isEditing ? (
        <input
          type="text"
          className="todo-edit-input"
          // biome-ignore lint/a11y/noAutofocus: focus required when entering edit mode.
          autoFocus
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitEdit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancelEdit();
            }
          }}
          onBlur={commitEdit}
          data-testid={`todo-edit-${t.id}`}
        />
      ) : (
        <button
          type="button"
          className="cl-text todo-label"
          onClick={() => startEdit(t)}
          disabled={readOnly}
          data-testid={`todo-label-${t.id}`}
        >
          {t.label}
        </button>
      )}
      {/* A done row carries neither control, so it reserves neither width: the
          divider above it already explains why the two groups differ. */}
      {readOnly ? null : handle}
      {!readOnly && deleteTodo ? (
        <button
          type="button"
          className="cl-del icon-btn-delete"
          onClick={() => deleteTodo(t.id)}
          aria-label="Slett"
          data-testid={`todo-delete-${t.id}`}
        >
          <Trash2 size={14} />
        </button>
      ) : null}
    </div>
  );
}
